/**
 * Shared mailbox types and mailbox-scoped sync API (FR4 / Story 9.2).
 * @see docs/openapi.json — MailboxStatusItemSchema
 */
import client from './client';
import type {
    MailboxStatusItem,
    MailboxSyncTriggerRequest,
    MailboxSyncTriggerResponse,
    SyncJob,
} from '../types/mailbox';

export type {
    MailboxConnectionState,
    MailboxErrorClass,
    MailboxStatusItem,
    MailboxSyncHealth,
    MailboxSyncTriggerRequest,
    MailboxSyncTriggerResponse,
    SyncJob,
    SyncJobStageEvent,
    SyncStage,
} from '../types/mailbox';

export { isTerminalSyncStage, TERMINAL_SYNC_STAGES } from '../types/mailbox';

export interface MailboxListResponse {
    mailboxes: MailboxStatusItem[];
    max_mailboxes?: number | null;
}

export type MailboxConnectMode = 'add' | 'reconnect';

/** SPA Gmail Settings used to abort at 8s and dump axios copy into the modal. */
export const MAILBOX_LIST_TIMEOUT_MS = 20000;

function isRetryableMailboxFetchError(err: unknown): boolean {
    const e = err as { code?: string; message?: string };
    const message = String(e?.message || '');
    return (
        e?.code === 'ECONNABORTED' ||
        e?.code === 'ERR_NETWORK' ||
        /timeout of \d+ms exceeded/i.test(message)
    );
}

export async function startMailboxGoogleOAuth(options: {
    connectMode: MailboxConnectMode;
    emailAddress?: string;
}): Promise<{ url?: string }> {
    const body: { connect_mode: MailboxConnectMode; email_address?: string } = {
        connect_mode: options.connectMode,
    };
    const trimmed = options.emailAddress?.trim();
    if (trimmed) {
        body.email_address = trimmed;
    }
    const response = await client.post<{ url?: string }>('/mailboxes/google/connect-url', body);
    return response.data ?? {};
}

async function getMailboxList(): Promise<MailboxListResponse> {
    const response = await client.get<MailboxListResponse>('/mailboxes', {
        timeout: MAILBOX_LIST_TIMEOUT_MS,
    });
    return response.data;
}

export async function fetchMailboxes(): Promise<MailboxListResponse> {
    try {
        return await getMailboxList();
    } catch (err) {
        if (!isRetryableMailboxFetchError(err)) {
            throw err;
        }
        return await getMailboxList();
    }
}

export const MAILBOX_SYNC_TRIGGERED_EVENT = 'domayn:mailbox-sync-triggered';

export async function triggerMailboxSync(
    mailboxId: number,
    body?: MailboxSyncTriggerRequest,
): Promise<MailboxSyncTriggerResponse> {
    const response = await client.post<MailboxSyncTriggerResponse>(
        `/mailboxes/${mailboxId}/sync`,
        body ?? {},
    );
    if (typeof window !== 'undefined') {
        window.dispatchEvent(
            new CustomEvent(MAILBOX_SYNC_TRIGGERED_EVENT, {
                detail: {
                    mailboxId,
                    syncJobId: response.data.sync_job_id,
                    status: response.data.status,
                },
            }),
        );
    }
    return response.data;
}

export async function fetchSyncJob(mailboxId: number, syncJobId: string): Promise<SyncJob> {
    const response = await client.get<SyncJob>(
        `/mailboxes/${mailboxId}/sync_jobs/${syncJobId}`,
        { timeout: 8000 },
    );
    return response.data;
}

function mailboxLooksConnected(mailbox: MailboxStatusItem): boolean {
    return (
        mailbox.connection_state === 'connected' ||
        String(mailbox.status || '').toUpperCase() === 'CONNECTED'
    );
}

/**
 * After Gmail OAuth succeeds, start sync for connected mailboxes that have
 * never completed a run. Reconnects that already have last_successful_sync
 * stay manual so adding a second mailbox does not re-sync the first.
 *
 * Failures are isolated per mailbox so a stale never-synced account cannot
 * block the mailbox that was just connected. If every attempted trigger
 * fails, the last error is rethrown so the OAuth-success UI can still ask
 * the user to retry.
 */
export async function startFirstMailboxSyncIfNeeded(): Promise<number[]> {
    const data = await fetchMailboxes();
    const started: number[] = [];
    const failures: unknown[] = [];
    for (const mailbox of data.mailboxes ?? []) {
        if (!mailboxLooksConnected(mailbox)) {
            continue;
        }
        if (mailbox.sync_in_progress) {
            continue;
        }
        if (mailbox.last_successful_sync) {
            continue;
        }
        try {
            await triggerMailboxSync(mailbox.id);
            started.push(mailbox.id);
        } catch (error) {
            failures.push(error);
            console.error(`First sync failed for mailbox ${mailbox.id}`, error);
        }
    }
    if (started.length === 0 && failures.length > 0) {
        throw failures[failures.length - 1];
    }
    return started;
}
